import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import { FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyNoticeDraft,
    clearNoticeDraft,
    createNoticeDraftPayload,
    NEW_NOTICE_DRAFT_KEY,
    readNoticeDraft,
    saveNoticeDraft,
} from './noticeDraftUtils'

const PANEL_SX = FORM_PANEL_SX
const getChoiceCardSx = getFormChoiceCardSx

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

function NoticeStatusSetting({ value, disabled, onChange }) {
    const options = [
        {
            value: 0,
            title: '미노출',
            description: '공지사항을 먼저 작성만 하고 사용자 화면에는 보여주지 않습니다.',
        },
        {
            value: 1,
            title: '노출',
            description: '저장 후 공지사항 목록과 상세 화면에 바로 표시합니다.',
        },
    ]

    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                상태
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = Number(value ?? 0) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio checked={checked} onChange={() => onChange(option.value)} disabled={disabled} />}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

export default function NewNotice() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText
    const listSearchSelect = locationState.searchSelect
    const listSearch = location.search || ''
    const draftKey = NEW_NOTICE_DRAFT_KEY
    const [data, setData] = useState({
        noticeId: null,
        title: '',
        titleSub: '',
        content: '',
        image: '',
        url: '',
        hit: 0,
        order: 0,
        status: 0,
    })
    const [saving, setSaving] = useState(false)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const initialDraftStateRef = useRef(null)
    const initialDraftSnapshotRef = useRef('')

    if (!initialDraftStateRef.current) {
        initialDraftStateRef.current = {
            data,
        }
        initialDraftSnapshotRef.current = JSON.stringify(createNoticeDraftPayload({
            data,
        }))
    }

    useEffect(() => {
        const storedDraft = readNoticeDraft(draftKey)
        const initialDraftState = initialDraftStateRef.current

        if (storedDraft && initialDraftState) {
            const restoredDraft = applyNoticeDraft({
                baseData: initialDraftState.data,
                draft: storedDraft,
            })

            setData(restoredDraft.data)
        }

        setDraftReady(true)
    }, [draftKey])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 공지사항 등록은 목록 query 대신 sessionStorage로 draft를 이어간다.
        const nextDraft = createNoticeDraftPayload({
            data,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearNoticeDraft(draftKey)
            return
        }

        saveNoticeDraft(draftKey, nextDraft)
    }, [data, draftKey, draftReady])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 등록 후에도 이전 공지 목록 페이지와 검색 조건을 그대로 복원한다.
    const moveToList = (snackbarState) => {
        clearNoticeDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/notices',
            search: listSearch,
            curPage: listCurPage,
            searchText: listSearchText,
            searchSelect: listSearchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value
        })
    }

    const handleStatusChange = (status) => {
        setData((prev) => ({
            ...prev,
            status,
        }))
    }

    const createData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/notice/create',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 1) {
                moveToList({ severity: 'success', message: '공지사항을 등록했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('공지사항을 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (data.title || '').trim()
        const nextContent = (data.content || '').trim()

        setData((prev) => ({
            ...prev,
            title: nextTitle,
            content: nextContent
        }))

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('제목을 입력하세요.')
            return
        }

        const formData = new FormData()
        formData.append('image', data.image)
        formData.append('title', nextTitle)
        formData.append('titleSub', data.titleSub)
        formData.append('content', nextContent)
        formData.append('url', data.url)
        formData.append('status', data.status)
        formData.append('order', data.order)

        createData(formData)
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>공지사항 등록</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={8}>
                            <FormSection
                                title="공지 내용"
                                description="일반 공지사항 목록과 상세에서 보여줄 기본 문구를 작성합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="제목"
                                        required
                                        id="title"
                                        name="title"
                                        value={data.title}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="내용"
                                        id="content"
                                        name="content"
                                        multiline
                                        rows={15}
                                        value={data.content}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormSection
                                title="노출 설정"
                                description="등록 후 바로 노출할지 임시 저장 성격으로 미노출할지 선택합니다."
                            >
                                <NoticeStatusSetting
                                    value={data.status}
                                    disabled={saving}
                                    onChange={handleStatusChange}
                                />
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
